import { isBefore } from 'date-fns';
import { useFormikContext } from 'formik';
import type { FC, InputHTMLAttributes, ReactNode } from 'react';
import { useEffect, useRef, useId, useState, useCallback } from 'react';
import { DateRangePopover } from 'src/components/form/datepicker/date-range-popover';
import {
  useIsInitiallyAfter,
  useIsInitiallyBefore,
} from 'src/components/form/datepicker/hooks';
import { DateInputField } from 'src/components/form/datepicker/input-field';
import type { InputTime } from 'src/components/form/datepicker/utils';
import {
  dateRegExp,
  dateTimeRegExp,
  isDateProvided,
  parseDateMatches,
  parseDateTimeMatches,
  transformValueToInputMask,
  validateDateInput,
} from 'src/components/form/datepicker/utils';
import { ErrorMessage } from 'src/components/form/error-message';
import { FieldGroup } from 'src/components/form/field-group';
import { FieldItem, FieldLayout } from 'src/components/form/field-layout';
import { FormHint } from 'src/components/form/form-hint';
import { Label } from 'src/components/form/label';
import { useCustomField } from 'src/components/form/use-custom-field';
import { useDefaultStacked } from 'src/hooks/use-default-stacked';
import { useDropdown } from 'src/hooks/use-dropdown';
import { cn } from 'src/utils/cn';
import type { StrictOmit } from 'src/utils/utility-types';

type DateRangePickerProps = {
  fromName: string;
  fromLabel?: string;
  toName: string;
  toLabel?: string;
  time?: InputTime | [InputTime, InputTime];
  fromInputProps?: StrictOmit<
    InputHTMLAttributes<HTMLInputElement>,
    'value'
  > & {
    disabledMessage?: ReactNode;
  };
  toInputProps?: StrictOmit<InputHTMLAttributes<HTMLInputElement>, 'value'> & {
    disabledMessage?: ReactNode;
  };
  stackedGroup?: boolean;
  stackedLabel?: boolean;
  minBookingDate?: Date;
  maxBookingDate?: Date;
  groupLabel?: string;
  hideLabel?: boolean;
  interval?: (fromDate: Date) => Date;
  fromHint?: ReactNode;
  toHint?: ReactNode;
  info?: ReactNode;
  /**
   * Appends an asterisk (*) to `fromLabel`.
   *
   * Should be set to true when this field is required, but there are
   * other fields in the containing form that are not required.
   *
   * If all fields in a form are required, the convention is to omit the
   * required marker.
   *
   * If you use this, you need to *manually* handle the asterisk for `groupLabel`!
   */
  fromMarkAsRequired?: boolean;
  /**
   * Appends an asterisk (*) to `toLabel`.
   *
   * Should be set to true when this field is required, but there are
   * other fields in the containing form that are not required.
   *
   * If all fields in a form are required, the convention is to omit the
   * required marker.
   *
   * If you use this, you need to *manually* handle the asterisk for `groupLabel`!
   */
  toMarkAsRequired?: boolean;
  initialOpen?: boolean;
  /**
   * Return `true` if the date should be blocked.
   *
   * This custom function is called _after_ the date was checked against
   * the min/max booking dates and `time` specific restrictions.
   */
  isDateBlocked?: (date: Date) => boolean;
};

export const DateRangePicker: FC<DateRangePickerProps> = ({
  time = false,
  fromName,
  fromLabel = 'From',
  toName,
  toLabel = 'To',
  fromInputProps,
  toInputProps,
  stackedGroup,
  stackedLabel,
  minBookingDate,
  maxBookingDate,
  groupLabel,
  hideLabel,
  interval,
  fromHint,
  toHint,
  info,
  fromMarkAsRequired,
  toMarkAsRequired,
  initialOpen,
  isDateBlocked,
}) => {
  const { isStackedLabel, isStackedGroup, minTablet } = useDefaultStacked(
    stackedLabel,
    stackedGroup
  );

  const { setFieldValue, setFieldTouched } = useFormikContext<unknown>();

  // only disable the field, if the initial value is before min booking date,
  // so the field does not become disabled, if the user accidentally enters a date which is too early,
  // so that the user has the chance to change it
  const fromDisabledBecauseInitiallyAfter = useIsInitiallyBefore(
    fromName,
    minBookingDate
  );

  // only disable the field, if the initial value is after max booking date,
  // so the field does not become disabled, if the user accidentally enters a date which is too early,
  // so that the user has the chance to change it
  const toDisabledBecauseInitiallyAfter = useIsInitiallyAfter(
    toName,
    maxBookingDate
  );

  const fromDisabled =
    fromInputProps?.disabled || fromDisabledBecauseInitiallyAfter;

  const toDisabled =
    toInputProps?.disabled ||
    Boolean(interval) ||
    toDisabledBecauseInitiallyAfter;

  const fromTime = Array.isArray(time) ? time[0] : time;
  const toTime = Array.isArray(time) ? time[1] : time;

  const fromId = useId();
  const fromUserValueRef = useRef('');
  const updateFromUserValueRef = (value: string) =>
    (fromUserValueRef.current = value);
  const [fromField, showFromFieldError, fromFieldError] = useCustomField<
    string | null
  >(fromName, fromLabel, {
    validate() {
      const fromValue = fromUserValueRef.current;
      const invalidDate = validateDateInput(fromTime, fromValue);
      if (invalidDate) return invalidDate;

      if (!toDisabled) return; // prefer showing invalid range errors on the end date
      const invalidRange = toAfterFrom({
        fromValue,
        toValue: toUserValueRef.current,
        fromTime,
        toTime,
      });
      if (invalidRange) return 'The start date cannot be after the end date.';
    },
  });

  const toId = useId();
  const toUserValueRef = useRef('');
  const updateToUserValueRef = (value: string) =>
    (toUserValueRef.current = value);

  const [toField, showToFieldError, toFieldError] = useCustomField<
    string | null
  >(toName, toLabel, {
    validate() {
      const toValue = toUserValueRef.current;
      const invalidDate = validateDateInput(toTime, toValue);
      if (invalidDate) return invalidDate;

      if (toDisabled) return; // prefer showing invalid range errors on the end date
      const invalidRange = toAfterFrom({
        fromValue: fromUserValueRef.current,
        toValue,
        fromTime,
        toTime,
      });
      if (invalidRange) return 'The end date must be after the start date.';
    },
  });

  const { active, setActive, setWrapperElement, ...dropdown } = useDropdown({
    placement: 'bottom-start',
    initialActive: initialOpen,
  });

  // Set initial value
  useEffect(() => {
    updateFromUserValueRef(
      transformValueToInputMask(fromField.value, fromTime)
    );
    updateToUserValueRef(transformValueToInputMask(toField.value, toTime));
  }, []); // oxlint-disable-line exhaustive-deps

  // field can be set from the outside, so we need to sync it with input
  useEffect(() => {
    const hasError = Boolean(
      validateDateInput(fromTime, fromUserValueRef.current)
    );
    // when there is no formik value, but an error, the user is probably still typing
    if (!fromField.value && hasError) return;
    const newValue = transformValueToInputMask(fromField.value, fromTime);
    if (newValue === fromUserValueRef.current) return;
    updateFromUserValueRef(newValue);
    setFieldValue(fromName, fromField.value, false); // even though the value has NOT changed we will trigger a rerender here as updating the value ref alone would not do it
  }, [fromField.value]); // oxlint-disable-line exhaustive-deps

  // field can be set from the outside, so we need to sync it with input
  useEffect(() => {
    const hasError = Boolean(validateDateInput(toTime, toUserValueRef.current));
    // when there is no formik value, but an error, the user is probably still typing
    if (!toField.value && hasError) return;
    const newValue = transformValueToInputMask(toField.value, toTime);
    if (newValue === toUserValueRef.current) return;
    updateToUserValueRef(newValue);
    setFieldValue(toName, toField.value, false); // even though the value has NOT changed we will trigger a rerender here as updating the value ref alone would not do it
  }, [toField.value]); // oxlint-disable-line exhaustive-deps

  // Append interval if given
  useEffect(() => {
    if (!interval) return;
    if (!fromField.value) return;

    setFieldValue(
      toName,
      interval(new Date(fromField.value)).toISOString(),
      true
    );
    // oxlint-disable-next-line exhaustive-deps
  }, [fromField.value, setFieldValue, toName, setActive]);

  const [focusedField, setFocusedField] = useState<'from' | 'to' | null>(null);

  const closeOnEscape = useCallback(() => {
    document.getElementById(focusedField === 'from' ? fromId : toId)?.focus();
    setActive(false);
  }, [fromId, toId, focusedField, setActive]);

  useEffect(() => {
    if (!active) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (document.activeElement === document.body && e.key === 'Escape')
        closeOnEscape();
    };

    document.body.addEventListener('keydown', handleEscape);
    return () => document.body.removeEventListener('keydown', handleEscape);
  }, [active, setActive, closeOnEscape]);

  const renderedFromHint = fromHint && (
    <FormHint isDisabled={fromDisabled}>{fromHint}</FormHint>
  );

  const renderedFromError = showFromFieldError && fromFieldError && (
    <ErrorMessage
      data-testid={`${fromField.name}Error`}
      error={fromFieldError}
      label={fromLabel}
    />
  );

  const fromDisplayedLabel = fromLabel + (fromMarkAsRequired ? '*' : '');
  const fromInput = (
    <FieldItem>
      <DateInputField
        referenceRef={!isStackedGroup ? dropdown.refs.setReference : undefined}
        time={fromTime}
        fieldId={fromId}
        name={fromName}
        value={fromUserValueRef.current}
        setUserValue={updateFromUserValueRef}
        showError={showFromFieldError}
        setActive={setActive}
        validate={(value) => validateDateInput(fromTime, value)}
        onFocus={() => setFocusedField('from')}
        onBlur={fromField.onBlur}
        aria-label={hideLabel ? fromDisplayedLabel : undefined}
        {...fromInputProps}
        disabled={fromDisabled}
        disabledMessage={fromInputProps?.disabledMessage}
        isoValue={fromField.value}
        showFocusStyle={active && focusedField === 'from'}
      />

      {renderedFromHint}

      {renderedFromError}
    </FieldItem>
  );

  const fromFieldItem =
    (groupLabel && minTablet) || hideLabel ? (
      fromInput
    ) : (
      <FieldLayout stacked={isStackedLabel}>
        <Label htmlFor={fromId} error={showFromFieldError}>
          {fromDisplayedLabel}
        </Label>

        {fromInput}
      </FieldLayout>
    );

  const renderedToHint = toHint && (
    <FormHint isDisabled={toDisabled}>{toHint}</FormHint>
  );

  const renderedToError = showToFieldError && toFieldError && (
    <ErrorMessage
      data-testid={`${toField.name}Error`}
      error={toFieldError}
      label={toLabel}
    />
  );

  const toDisplayedLabel = toLabel + (toMarkAsRequired ? '*' : '');
  const toInput = (
    <FieldItem>
      <DateInputField
        referenceRef={isStackedGroup ? dropdown.refs.setReference : undefined}
        time={toTime}
        fieldId={toId}
        name={toName}
        value={toUserValueRef.current}
        setUserValue={updateToUserValueRef}
        showError={showToFieldError}
        setActive={setActive}
        validate={(value) => validateDateInput(toTime, value)}
        onFocus={() => setFocusedField('to')}
        onBlur={toField.onBlur}
        onTab={() => setActive(false)}
        aria-label={hideLabel ? toDisplayedLabel : undefined}
        {...toInputProps}
        disabled={toDisabled}
        disabledMessage={toInputProps?.disabledMessage}
        isoValue={toField.value}
        showFocusStyle={active && focusedField === 'to'}
      />

      {renderedToHint}

      {renderedToError}
    </FieldItem>
  );

  const toFieldItem =
    (groupLabel && minTablet) || hideLabel ? (
      toInput
    ) : (
      <FieldLayout stacked={isStackedLabel}>
        <Label htmlFor={toId} error={showToFieldError}>
          {toDisplayedLabel}
        </Label>

        {toInput}
      </FieldLayout>
    );

  return (
    <div
      ref={setWrapperElement}
      onKeyDown={(e) => {
        if (e.key === 'Escape') closeOnEscape();
      }}
    >
      {groupLabel && minTablet && !hideLabel ? (
        <FieldGroup
          label={groupLabel}
          stacked={isStackedLabel}
          stackedChildren={isStackedGroup}
          id={fromId}
          info={info}
        >
          {fromFieldItem}
          {toFieldItem}
        </FieldGroup>
      ) : (
        <div
          className={cn(
            `
              grid items-start gap-md
              legacy:gap-[1rem]
            `,
            isStackedGroup ? 'grid-flow-row' : 'grid-flow-col'
          )}
        >
          {fromFieldItem}
          {toFieldItem}
        </div>
      )}

      {active && (
        <DateRangePopover
          setFloating={dropdown.refs.setFloating}
          floatingStyle={dropdown.style}
          fromDisabled={fromDisabled}
          toDisabled={toDisabled}
          time={time}
          value={{
            from: fromField.value ? new Date(fromField.value) : null,
            to: toField.value ? new Date(toField.value) : null,
          }}
          minBookingDate={minBookingDate}
          maxBookingDate={maxBookingDate}
          handleSelect={(date) => {
            const startDateString = date.from && date.from.toISOString();

            if (startDateString !== fromField.value) {
              updateFromUserValueRef(
                transformValueToInputMask(startDateString, fromTime)
              );
              setFieldTouched(fromName, true, false);
              setFieldValue(fromName, startDateString, true);
              // whenever the user selects a "from" date, we want to focus the to field
              setFocusedField('to');
            }

            const endDateString = date.to && date.to.toISOString();

            if (endDateString !== toField.value) {
              updateToUserValueRef(
                transformValueToInputMask(endDateString, toTime)
              );
              // only set "to" field as touched, if endDateString is not null
              // (if it is null the user is in the middle of a selection and we should not
              // show errors)
              setFieldTouched(toName, endDateString !== null, false);
              setFieldValue(toName, endDateString, true);
            }
          }}
          fromHint={renderedFromHint}
          toHint={renderedToHint}
          fromError={renderedFromError}
          toError={renderedToError}
          isDateBlockedCustom={isDateBlocked}
        />
      )}
    </div>
  );
};

function toAfterFrom({
  fromValue,
  toValue,
  fromTime,
  toTime,
}: {
  fromValue: string | null;
  toValue: string | null;
  fromTime: InputTime;
  toTime: InputTime;
}) {
  if (!isDateProvided(fromTime, fromValue)) return;
  if (!isDateProvided(toTime, toValue)) return;
  if (validateDateInput(fromTime, fromValue)) return;
  if (validateDateInput(toTime, toValue)) return;

  const fromMatches = fromValue.match(fromTime ? dateTimeRegExp : dateRegExp);
  if (!fromMatches) return;
  const fromDate = fromTime
    ? parseDateTimeMatches(fromMatches)
    : parseDateMatches(fromMatches);

  const toMatches = toValue.match(toTime ? dateTimeRegExp : dateRegExp);
  if (!toMatches) return;
  const toDate = toTime
    ? parseDateTimeMatches(toMatches)
    : parseDateMatches(toMatches);

  return isBefore(toDate, fromDate);
}
